/**
 * UTM utilities — capture from URL on landing, persist in cookies, attach to leads.
 *
 * Behavior:
 * - On landing, if the URL has any UTM, ALL previous UTM cookies are cleared
 *   and the new ones are stored (atomic replace). This avoids mixing campaigns.
 * - Once stored, the UTMs stay in the cookies (up to `UTM_COOKIE_DAYS`) no
 *   matter where the user navigates on the site — plain navigations do NOT
 *   clear them anymore. `UtmCapture` (the component that calls this) is the
 *   one that repaints them back onto the visible URL on every page.
 * - `getUtms()` reads the current UTMs: from the URL if present, otherwise
 *   falls back to whatever is stored in cookies.
 */

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

export const UTM_COOKIE_DAYS = 30;
export const UNIT_KEYS = ['unidad', 'unit'];

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const setCookie = (name, value, days) => {
  if (!isBrowser()) return;
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  // SameSite=Lax keeps cookies on top-level navigations (typical landing flow)
  document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/; SameSite=Lax`;
};

const deleteCookie = (name) => {
  if (!isBrowser()) return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
};

const readCookie = (name) => {
  if (!isBrowser()) return null;
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  if (!match) return null;
  const raw = match.slice(name.length + 1);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

/**
 * Read UTMs from a search string (e.g. window.location.search).
 * Returns an object containing only present, non-empty UTM keys.
 */
export const parseUtmsFromSearch = (search) => {
  if (!search) return {};
  const params = new URLSearchParams(search);
  const out = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v && v.trim()) out[key] = v.trim();
  }
  return out;
};

export const hasUnitParam = (search) => {
  if (!search) return false;
  const params = new URLSearchParams(search);

  return UNIT_KEYS.some((key) => {
    const value = params.get(key);
    return Boolean(value && value.trim());
  });
};

const clearStoredUtms = () => {
  UTM_KEYS.forEach(deleteCookie);
};

/**
 * If the current URL carries any UTM, clear previous UTM cookies and store
 * the incoming set (fresh campaign hit wins). If the URL has no UTM, the
 * previously stored ones (if any) are left untouched.
 */
export const captureUtmsFromUrl = (search = isBrowser() ? window.location.search : '') => {
  const incoming = parseUtmsFromSearch(search);
  if (Object.keys(incoming).length === 0) {
    return null;
  }

  // Atomic replace: clear all previous UTM cookies first
  clearStoredUtms();

  // Persist incoming UTMs
  Object.entries(incoming).forEach(([k, v]) => setCookie(k, v, UTM_COOKIE_DAYS));

  return incoming;
};

/**
 * Read all currently-stored UTMs as a flat object, straight from the
 * cookies, regardless of what the current URL looks like.
 */
export const getStoredUtms = () => {
  const out = {};
  for (const key of UTM_KEYS) {
    const v = readCookie(key);
    if (v) out[key] = v;
  }
  return out;
};

/**
 * Read the current UTMs: from the URL if it carries any, otherwise falls
 * back to whatever is stored in cookies.
 */
export const getUtms = (search = isBrowser() ? window.location.search : '') => {
  const incoming = parseUtmsFromSearch(search);
  if (Object.keys(incoming).length > 0) {
    return incoming;
  }

  return getStoredUtms();
};
